import { ComponentType } from '@angular/cdk/overlay';
import { MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Quaternion, Vector3, AbstractMesh, Color4 } from '@babylonjs/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IV_VIEW_TYPES } from '@root/app.config';
import { SelectOption } from '@shared/form-fields/select/select.component';

/** To extend window you must extend the global Window interface */
declare global {
	interface Window {
		URL: typeof URL;
		WritableStream: typeof WritableStream;
	}
}

export type ProductType = 'outdoor' | 'interior';

export interface Breakpoint {
	min: string;
	max: string;
}

export interface Breakpoints {
	[propName: string]: Breakpoint;
}

export interface NetworkStatus {
	event: Event;
	status: boolean;
}

export interface GoogleMapsInfoWindow extends google.maps.InfoWindow {
	getMap?: () => google.maps.Map | null;
}

export interface DialogOptions<D> extends MatDialogConfig {
	component: ComponentType<any>;
	data?: D;
}

export interface BlindSetup {
	frames?: Frames;
	material?: Material;
	mounting?: Mounting;
	operation?: Operation;
	size?: Size;
	fixtures_color?: FixturesColor;
}

export interface Frames {
	bottom_bar: BottomBar;
	frame_color: FrameColor;
	top_style: TopStyle;
	optionals?: BottomChannel[];
}

export type BlindConfigSetupItem = Material | Size | Operation | FrameColor | TopStyle | Mounting;

export interface Color extends DefaultProperties {
	color: string;
	optionals?: BottomChannel[];
}

export interface FrameColor extends Color {
	color_name?: string;
	is_custom?: boolean;
	last_saved_color?: string;
	configured?: boolean;
}

export interface FrameColorConfig extends DefaultProperties {
	color: string;
	is_default: boolean;
	product_type: ProductType;
	status: string;
}

export interface Material extends Color {
	color_name: string;
	opacity: number;
	type: string;
	configured?: boolean;
	is_extra_fee?: boolean;
}

export interface ServerColour {
	id: number;
	name: string;
	color: string;
	is_ziptrak_color: boolean;
	is_default: boolean;
	sort_order: number;
}

export interface ServerMaterial {
	available_colors: ServerColour[];
	color: { default?: string; can_be_changed?: boolean; activeColor?: string };
	id: number;
	is_default: boolean;
	is_extra_fee: boolean;
	name: string;
	opacity: number;
	product_type: string;
	sizes: { width: { default: string; maximum: string; minimum: string }; height: { default: string; maximum: string; minimum: string } };
	sort_order: number;
	type: string;
	texture: string;
}

export interface FixturesColor extends Color {
	checked: boolean;
	is_default: boolean;
}

export interface Size {
	width: number;
	height: number;
	extra_size?: boolean;
	configured?: boolean;
}

export interface TopStyle extends DefaultProperties {
	description?: string;
	image?: string | null;
	is_default?: boolean;
	status?: string | null;
	type?: string;
	configured?: boolean;
	is_extra_fee?: boolean;
}

export interface BottomBar extends TopStyle {}

export interface BottomChannel extends DefaultProperties {
	is_checked: boolean;
}

export interface Mounting extends DefaultProperties {
	checked: boolean;
	description: string;
	is_default: boolean;
	configured?: boolean;
	is_extra_fee?: boolean;
}

export interface Operation extends DefaultProperties {
	default_option: string;
	is_default: boolean;
	long_name: string;
	optional: OperationOption[];
	checked?: boolean;
	configured?: boolean;
	is_extra_fee?: boolean;
}

export interface OperationOption extends DefaultProperties {
	description: string;
	operation_id: string;
	product_type: string;
	is_extra_fee?: boolean;
}

export interface BlindData {
	blind_id: number;
	default_name: string;
	type: string;
	name?: string;
	has_name?: boolean;
	setup?: BlindSetup;
	blind_img?: string;
	blind_img_url?: string;
	iv_img?: string;
	meta?: BlindMeta;
}

export interface BlindProperties {
	id: number;
	type: string;
	buttonPressed?: boolean;
}

export interface DefaultProperties {
	id: number;
	name: string;
}

export interface BlindMeta {
	is_auto_created?: boolean;
	change_time?: null | Date;
}

export interface ImageData {
	country: string;
	image: string;
	name: string;
}

export interface Retailer {
	address: string;
	car_button: string;
	country: string;
	date: string;
	display_coeff: string;
	display_count: string;
	icon: string;
	id: number;
	latitude: string;
	logo: string;
	longitude: string;
	name: string;
	page_id: string;
	phone: string;
	premium: string;
	priority: string;
	product: string;
	retailers_page: string;
	service_and_repairs: string;
	show_address: boolean;
	status: string;
	store_type: string;
	swiftee: string;
	website: string;
	zip_code: string;
	suburb: string;
	state: string;
	marker: any;
	full_address: string;
	boost?: number;
	open?: boolean;
}

export interface AccordionSelector {
	title: string;
	content: string;
	item: string;
}

export interface BoundingBoxSettings {
	scaling: Vector3;
	position: Vector3;
	rotationQuaternion: Quaternion;
	cameraRadius: number;
	isEnabled: boolean;
}

export interface BoundingBoxesSettings {
	[blindId: string]: BoundingBoxSettings;
}

export interface RootMeshesByBlindType {
	outdoor: AbstractMesh;
	interior: AbstractMesh;
}

export interface GizmoMinMaxMeshScaling {
	min: { x: number; y: number };
	max: { x: number; y: number };
}

export interface ZoomGizmoSettings {
	fingers: number;
	pickOnMesh: boolean;
}

export interface QuizCloseButton {
	status: boolean;
	reviewed?: boolean;
}

export interface ShutterValues {
	id: string;
	value: number;
}

interface TaskbarButton {
	type: string;
	text_translation: string;
}

export interface TaskbarItem {
	title_translation: string;
	id: string;
	active: boolean;
	status: null | boolean;
	button: TaskbarButton;
}

export enum SceneTypes {
	DYB = 'dyb',
	IV = 'iv',
}

export type SceneColors = Record<SceneTypes, Color4>;

export type IVViewType = (typeof IV_VIEW_TYPES)[keyof typeof IV_VIEW_TYPES];

export type BlindScreenShots = {
	name: string;
	image: string;
};

export interface ScreenshotObserve {
	img: string;
	blindId?: number;
}

export interface ScreenShotsObjects {
	sceneScreenShot: string;
	blindScreenShots?: BlindScreenShots;
}

export interface ImageVisualisationSettings {
	height: number;
	width: number;
	type: string | null | undefined;
	index: number | null | undefined;
	image: string;
	shutterValues?: { [blindId: string]: number };
	settings?: BoundingBoxesSettings;
}

export interface SVGData {
	name: string;
	file: SafeResourceUrl;
}

export interface BackgroundConfig {
	src: string;
	night_src: string;
	width: number;
	height: number;
	type: string;
	index: number;
	theme: string;
	image_orientation: string;
	id: string;
	image?: string | ArrayBuffer;
	base64: string | ArrayBuffer;
	blob: File | Blob;
	shutterValues?: { [blindId: string]: number };
	settings?: BoundingBoxesSettings;
	sampleProjectBlinds?: { [blindId: string]: number };
	cropped_src: string;
	cropper_settings: CropperSettings;
}

export interface ImageCategory {
	preview: string[];
	landscape: string[];
	portrait: string[];
	origin: string[];
}

export interface ProjectSamplesImages {
	outdoor: {
		day: ImageCategory;
		night: ImageCategory;
	};
	interior: {
		day: ImageCategory;
	};
}

export interface LoadedImageData {
	src: string;
	width: number;
	height: number;
}

export interface ImageSize {
	width: number;
	height: number;
}

export interface CroppedBackgroundConfig {
	cropped_src: string;
	width: number;
	height: number;
}

export interface IVSceneSettings {
	sample: BackgroundConfig;
	upload: BackgroundConfig;
	iv_last_type: string;
	device: string;
	openings: Opening[];
	detectionImageSize: ImageSize;
}

export interface IBlindSelect {
	id: number | string;
	type?: string;
	status?: string;
}

export interface Country {
	code: string;
	name: string;
	data_store: string;
	link: string;
	is_default?: boolean;
	locale: string;
	currency: string;
}

export interface FarSettings {
	country: string;
	country_code: string;
	country_latitude: number;
	country_longitude: number;
	description_for_postcode: string;
	description_for_retailer_name: string;
	filters_are_enabled: boolean;
	max_non_premium_distance: number;
	max_premium_distance: number;
	non_premium_max_additional_search_quantity: number;
	non_premium_number: number;
	number_of_retailers_can_be_selected: number;
	premium_display: boolean;
	premium_number: number;
	show_premium_retailers_only: boolean;
	standart_non_premium_distance: number;
	subtitle: string;
	title: string;
	duplicate_premium_retailers: null | string;
}

export enum CropperAction {
	ROTATE = 'ROTATE',
	ZOOM_IN = 'ZOOM_IN',
	ZOOM_OUT = 'ZOOM_OUT',
	RESET = 'RESET',
	DELETE = 'DELETE',
	UPLOAD = 'UPLOAD',
}

export enum BackgroundImageModalAction {
	Exit = 'exit',
	Edit = 'edit',
	Add = 'add',
}

export enum HeaderShopMenu {
	Home = 'home',
	Overview = 'overview',
	Quote = 'quote',
	Deposit = 'deposit',
}

export interface CropperSettings {
	cropper_data: Cropper.Data;
	cropper_box_data: Cropper.CropBoxData;
	cropper_canvas_data: Cropper.CanvasData;
	cropper_container_data: Cropper.ContainerData;
}

export interface Rating {
	id: string;
	score: number;
}

export interface FeedbackData {
	session_key: string;
	rating: number;
	feedback?: string;
}

export interface PostDataPayload {
	file_type?: string;
	file_key?: string;
	task_uuid?: string;
	type?: string;
	additional_parameter?: {
		is_indoor?: number;
	};
	sha256?: string;
}

export interface DetectionApiResponse<T = unknown> {
	status: 0 | 1;
	msg: string;
	data: T | null;
	token: string;
}

export interface PresignedUrlResponse {
	key: string;
	url: string;
	file_already_uploaded: boolean;
}

export interface StoredDetectionResults {
	results: DetectionResult;
	timeout?: boolean;
}

export interface Point {
	x: number;
	y: number;
}

export interface Opening {
	id: number;
	coordinates: Point[];
	isSelected: boolean;
	isFitted: boolean;
	blindId: number | null;
	displayNumber: number | null;
	name: string | null;
	blindType: string | null;
}

export interface ModalBlindData {
	blindId: number;
	displayNumber: number;
	selected: boolean;
	type: string;
	name: string;
	openingId?: number;
}

export type DetectionPoint = { x: number; y: number }; // Not sure if this is the correct way or place to put these.
export type DetectionResult = Array<Array<DetectionPoint>> | null;

export interface ActionData {
	session_key: string;
	action_type: string;
	element: string;
	page_url: string;
	device_info: string;
	created_at: Date;
}

export enum BlindCreateAction {
	Outdoor = 'create_outdoor_blind',
	Interior = 'create_interior_blind',
}

export interface RetailerClickAction {
	action: RetailerClickActions;
	retailer_id: Retailer['id'];
	origin: 'DYB';
}

export enum RetailerClickActions {
	MAP_ICON = 'click_map_icon',
	SELECT_RETAILER = 'click_retailer',
}

export type SessionData = ContactForm & {
	action: string;
	center_latitude: number | null;
	center_longitude: number | null;
	retailers: any;
	country: string;
	session_key: string;
	embed_key: string;
	origin: string;
	blinds: BlindData[];
	iv_screenshot: string | null;
	time: number;
};

export interface NodeRef {
	group: SVGGElement;
	hit: SVGCircleElement;
	dot: SVGCircleElement;
}

export interface BlindDisplayStatus {
	show: boolean;
	previouslyAssigned: boolean;
}

export interface PhotoForm {
	photo: FormControl<string>;
	photo_base64: FormControl<string | ArrayBuffer | null>;
	photo_src: FormControl<string | null>;
	photo_blob: FormControl<Blob | null>;
	cropped_src: FormControl<string | null>;
	night_src: FormControl<string | null>;
}

export enum SectionKeysEnum {
	Material = 'material',
	Size = 'size',
	FrameColor = 'frame-color',
	TopStyle = 'top-style',
	BottomBar = 'bottom-bar',
	Operation = 'operation',
	Mounting = 'mounting',
	ColorOfFixtures = 'color-of-fixtures',
}
export type SectionKey = SectionKeysEnum;

export type BlindConfigurationStates = Record<number, Set<SectionKey>>;

export type BlindConfigurationStatuses = Record<number, boolean>;

// Overview Blind Gallery
export type BlindType = BlindTypes;
export enum BlindTypes {
	Outdoor = 'outdoor',
	Interior = 'interior',
}

export type GalleryViewType = GalleryViewTypes;
export enum GalleryViewTypes {
	Default = 'default',
	Shop = 'shop',
}

export interface ContactForm {
	first_name: string;
	last_name: string;
	phone: string;
	post_code: string;
	email: string;
	confirm_email?: string;
	comments: null | string;
	contact: string;
}

export type QuotationForm = Pick<ContactForm, 'first_name' | 'last_name' | 'phone' | 'email' | 'post_code' | 'comments' | 'contact'> & {
	installation_address: string;
	country: Country;
	suburb: string;
	state: SelectOption;
};

export type QuotationFormPayload = Omit<QuotationForm, 'country' | 'state'> & {
	country: string;
	state: string;
};

type ControlsOf<T> = {
	[K in keyof T]: FormControl<T[K] | null>;
};

export type QuotationFormGroup = FormGroup<ControlsOf<QuotationForm>>;

export interface DialogState {
	id: UUID;
	isOpen: boolean;
}

export type UUID = `${string}-${string}-${string}-${string}-${string}`;

export interface BlindPartsAvailability {
	blindId: number;
	availability: PartsAvailability;
}

export interface BlindsValidity {
	[id: number]: BlindValidityStatus;
}

export interface BlindValidityStatus {
	block: boolean;
	warning: boolean;
}

export type PartsAvailability = {
	[sectionKey in keyof BlindSetup]: PartStatusMap[];
};

export type PartStatusMap = {
	partId: string | number;
	status: PartValidationSeverity;
	valid: boolean;
	message: string | null;
};

export type PartRules = {
	[sectionKey in keyof BlindSetup]?: PartRule[];
};

export type PartRule = {
	id: string | number;
	validate: (blind: BlindData) => PartValidationResult;
	translation_key: PartRuleTranslationKey;
};

export interface PartValidationResult {
	valid: boolean;
	severity: PartValidationSeverity;
}

export enum PartValidationSeverity {
	Allow = 'allow',
	Warning = 'warning',
	Block = 'block',
}

export type PartRuleTranslationKey = string | { [blindType in BlindTypes]: string };

export enum MaterialType {
	Sunscreen = '95%_sunscreen_mesh',
	ClearPVC = 'clear_pvc',
}

export interface SendCodeData {
	email: string;
}

export interface VerifyCodeData extends SendCodeData {
	code: string;
}

export interface VerificationResponse {
	success: boolean;
	message: string;
}

export interface PriceDetails {
	blinds: Price[];
	total_price: number;
	deposit: number;
}

export interface Price {
	blind_id: number;
	quotation: QuotationPrice;
}

export interface QuotationPrice {
	material_price: null | number;
	material_color_price: null | number;
	operation_price: null | number;
	pelmet_price: null | number;
	bottom_bar_price: null | number;
	mounting_price: null | number;
	operation_optionals_price: null | number;
	total_price: number;
}

export interface LoaderState {
	visible: boolean;
	classes: string[];
}

export type PlaceSuggestion = {
	prediction: google.maps.places.PlacePrediction;
	text: string;
};

export interface AccessResponse {
	access: number;
	search_count: number;
}
